// FILE: src/components/QuestionNavigator.js
//
// Clickable question navigation panel for the exam screen.
//
// Renders a scrollable grid of numbered circles, each colour-coded by the
// question's current status, and a legend below.  Clicking any circle
// navigates directly to that question via the onJumpTo callback.
//
// ── Status derivation ─────────────────────────────────────────────────────────
//
//   Status is derived purely from existing session state — no extra per-question
//   database fields are required.  Priority (highest wins):
//
//   'current'          index === currentIndex
//   'answered-flagged' submitted AND flagged
//   'answered'         questionStates[i]?.submitted === true
//   'skipped-flagged'  skipped AND flagged
//   'skipped'          skippedIndices.has(i)  (and not submitted)
//   'flagged'          flaggedIndices.has(i)  (not submitted, not skipped)
//   'unseen'           default
//
// ── Props ─────────────────────────────────────────────────────────────────────
//
//   totalQuestions  number    — total questions in the session
//   currentIndex    number    — 0-based index of the currently visible question
//   questionStates  object    — { [index]: { submitted: boolean, ... } }
//   skippedIndices  Set       — question indices skipped and not yet answered
//   flaggedIndices  Set       — question indices flagged for review
//   onJumpTo        function  — called with (index) to navigate directly
//   isVisible       boolean   — controls collapsed / expanded state
//   onToggle        function  — called to toggle visibility

import React, { useState } from 'react';
import css from './QuestionNavigator.module.css';

// ── Status → visual style map ─────────────────────────────────────────────────

const STATUS_COLORS = {
  unseen:                       { bg: '#f0f0f0', border: '#ccc',    text: '#555',  label: 'Unseen'    },
  current:                      { bg: '#1a1a2e', border: '#1a1a2e', text: '#fff',  label: 'Current'   },
  answered:                     { bg: '#2e7d32', border: '#2e7d32', text: '#fff',  label: 'Answered'  },
  'answered-flagged':           { bg: '#2e7d32', border: '#1565c0', text: '#fff',  label: 'Ans + Flag'},
  // Phase 3.6+ — correctness reveal in Practice / Targeted modes only.
  'answered-correct':           { bg: '#2e7d32', border: '#2e7d32', text: '#fff',  label: 'Correct'   },
  'answered-correct-flagged':   { bg: '#2e7d32', border: '#1565c0', text: '#fff',  label: 'Right + Flag'},
  'answered-incorrect':         { bg: '#c62828', border: '#c62828', text: '#fff',  label: 'Incorrect' },
  'answered-incorrect-flagged': { bg: '#c62828', border: '#1565c0', text: '#fff',  label: 'Wrong + Flag'},
  skipped:                      { bg: '#e07b00', border: '#e07b00', text: '#fff',  label: 'Skipped'   },
  'skipped-flagged':            { bg: '#e07b00', border: '#1565c0', text: '#fff',  label: 'Skip + Flag'},
  flagged:                      { bg: '#1565c0', border: '#1565c0', text: '#fff',  label: 'Flagged'   },
};

// Legend rows differ between Full Exam (uniform answered) and Practice/Targeted
// (split into correct/incorrect).  The render path picks the right list based
// on the `revealCorrectness` prop.
const LEGEND_STATUSES_FULL    = ['unseen','current','answered','skipped','flagged','answered-flagged'];
const LEGEND_STATUSES_REVEAL  = ['unseen','current','answered-correct','answered-incorrect','skipped','flagged'];

// ── Status derivation helper ──────────────────────────────────────────────────

export function getStatus(
  index,
  currentIndex,
  questionStates,
  skippedIndices,
  flaggedIndices,
  // Phase 3.6+ — optional inputs.  When omitted by older callers the function
  // collapses back to the historical Skipped/Answered/Flagged buckets.
  everSkippedIndices = skippedIndices,
  revealCorrectness  = false,
  questions          = null,
) {
  if (index === currentIndex) return 'current';

  const state      = questionStates[index];
  const isAnswered = state?.submitted === true;
  const isFlagged  = flaggedIndices.has(index);
  // Use everSkippedIndices so a question that was skipped and later
  // dismissed via re-skip in the revisit pass still shows as 'skipped'
  // until the learner actually answers it.  Existing callers can pass
  // skippedIndices for both args and behaviour matches the old logic.
  const isSkipped  = !isAnswered && (everSkippedIndices?.has?.(index) === true || skippedIndices.has(index));

  if (isAnswered) {
    // Reveal mode: split into correct vs. incorrect using the selected
    // answers in questionStates against the question's correctAnswers.
    if (revealCorrectness && Array.isArray(questions) && questions[index]) {
      const q       = questions[index];
      const sel     = Array.isArray(state?.selectedAnswers) ? state.selectedAnswers : [];
      const correct = Array.isArray(q.correctAnswers) ? q.correctAnswers : [];
      const isCorrect =
        sel.length === correct.length &&
        correct.every((id) => sel.includes(id));
      if (isCorrect && isFlagged) return 'answered-correct-flagged';
      if (isCorrect)              return 'answered-correct';
      if (isFlagged)              return 'answered-incorrect-flagged';
      return 'answered-incorrect';
    }
    if (isFlagged) return 'answered-flagged';
    return 'answered';
  }

  if (isSkipped && isFlagged) return 'skipped-flagged';
  if (isSkipped)              return 'skipped';
  if (isFlagged)              return 'flagged';
  return 'unseen';
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function QuestionNavigator({
  totalQuestions,
  currentIndex,
  questionStates,
  skippedIndices,
  flaggedIndices,
  // Phase 3.6+ — optional, with defaults that preserve historical behaviour.
  everSkippedIndices,             // when omitted, falls back to skippedIndices
  revealCorrectness = false,      // when true, answered = green/red split
  questions = null,               // session.questions[] — needed for correctness
  onJumpTo,
  isVisible,
  onToggle,
  isDrawer = false,
  // PMI-style exam breaks (Simulation only): question indices below this
  // threshold are locked — viewable but no longer editable. 0 = none locked.
  lockedThreshold = 0,
}) {
  // Drawer open/close state (only used when isDrawer=true)
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Mini progress summary counts
  const answeredCount = Object.values(questionStates).filter((s) => s?.submitted).length;
  // Display skipped count counts every question that's currently shown as
  // 'skipped' in the grid — the union of skippedIndices (still-pending in
  // the queue) and everSkippedIndices (ever-skipped + not answered).
  const visibleSkipped = new Set(skippedIndices);
  if (everSkippedIndices) {
    for (const idx of everSkippedIndices) {
      if (!questionStates[idx]?.submitted) visibleSkipped.add(idx);
    }
  }
  const skippedCount  = visibleSkipped.size;
  const flaggedCount  = flaggedIndices.size;
  const unseenCount   = totalQuestions - answeredCount - skippedCount;

  function handleJumpTo(i) {
    onJumpTo(i);
    if (isDrawer) setDrawerOpen(false); // close drawer after navigation
  }

  // ── Shared navigator content ────────────────────────────────────────────
  const navigatorContent = (
    <div style={S.wrapper} data-testid="question-navigator">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={S.header}>
        <span style={S.headerTitle}>Navigator</span>
        {!isDrawer && (
          <button
            style={S.toggleBtn}
            onClick={onToggle}
            title={isVisible ? 'Collapse navigator' : 'Expand navigator'}
            aria-expanded={isVisible}
            aria-label="Toggle question navigator"
          >
            {isVisible ? '▲' : '▼'}
          </button>
        )}
        {isDrawer && (
          <button
            className={css.drawerClose}
            onClick={() => setDrawerOpen(false)}
            aria-label="Close navigator"
            data-testid="nav-drawer-close"
          >
            ✕
          </button>
        )}
      </div>

      {(isDrawer || isVisible) && (
        <>
          {/* ── Progress summary ──────────────────────────────────────── */}
          <div style={S.summary} className={css.summary}>
            <span style={S.summaryItem('answered')}>{answeredCount} done</span>
            {skippedCount  > 0 && <span style={S.summaryItem('skipped')}>{skippedCount} skipped</span>}
            {flaggedCount  > 0 && <span style={S.summaryItem('flagged')}>{flaggedCount} flagged</span>}
            <span style={S.summaryItem('unseen')}>{Math.max(unseenCount, 0)} unseen</span>
          </div>

          {/* ── Question number grid ─────────────────────────────────── */}
          <div style={S.grid} className={css.grid}>
              {Array.from({ length: totalQuestions }, (_, i) => {
                const status   = getStatus(
                  i, currentIndex, questionStates, skippedIndices, flaggedIndices,
                  everSkippedIndices, revealCorrectness, questions,
                );
                const colors   = STATUS_COLORS[status];
                const isCurrent = i === currentIndex;
                const isFlaggedQ = flaggedIndices.has(i);
                const isLockedQ  = i < lockedThreshold;
                const showFlagDot = isFlaggedQ && status !== 'flagged' && !isLockedQ;

                return (
                  <button
                    key={i}
                    data-testid={`nav-q-${i + 1}`}
                    className={css.circle}
                    style={{
                      ...S.circle,
                      background: colors.bg,
                      color:      colors.text,
                      border:     `2px solid ${colors.border}`,
                      fontWeight: isCurrent ? '800' : '600',
                      boxShadow:  isCurrent
                        ? '0 0 0 2px #fff, 0 0 0 4px #1a1a2e'
                        : 'none',
                      transform: isCurrent ? 'scale(1.18)' : 'scale(1)',
                      zIndex: isCurrent ? 1 : 0,
                      // Locked questions stay clickable (viewable) but read dimmed.
                      opacity: isLockedQ && !isCurrent ? 0.55 : 1,
                    }}
                    onClick={() => handleJumpTo(i)}
                    title={`Q${i + 1}${isFlaggedQ ? ' (flagged)' : ''}${isLockedQ ? ' (locked)' : ''} — ${status}`}
                    aria-label={`Go to question ${i + 1}`}
                    aria-current={isCurrent ? 'step' : undefined}
                  >
                    {i + 1}
                    {isLockedQ && <span style={S.flagPip} aria-hidden="true">🔒</span>}
                    {showFlagDot && <span style={S.flagPip} aria-hidden="true">🚩</span>}
                    {status === 'flagged' && !isLockedQ && <span style={S.flagPip} aria-hidden="true">🚩</span>}
                  </button>
                );
              })}
          </div>

          {/* ── Legend ──────────────────────────────────────────────── */}
          <div style={S.legend} className={css.legend}>
            {(revealCorrectness ? LEGEND_STATUSES_REVEAL : LEGEND_STATUSES_FULL).map((status) => (
              <div key={status} style={S.legendItem}>
                <div
                  style={{
                    ...S.legendDot,
                    background: STATUS_COLORS[status].bg,
                    border: `2px solid ${STATUS_COLORS[status].border}`,
                  }}
                />
                <span style={S.legendLabel}>{STATUS_COLORS[status].label}</span>
              </div>
            ))}
            {lockedThreshold > 0 && (
              <div style={S.legendItem} data-testid="legend-locked">
                <span style={S.legendLabel}>🔒 Locked (pre-break)</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  // ── Drawer mode (tablet/mobile) ─────────────────────────────────────────
  if (isDrawer) {
    return (
      <>
        {/* Floating toggle button */}
        <button
          className={css.drawerToggle}
          onClick={() => setDrawerOpen((v) => !v)}
          aria-label="Open question navigator"
          data-testid="nav-drawer-toggle"
        >
          ☰
        </button>

        {/* Overlay */}
        <div
          className={`${css.drawerOverlay} ${drawerOpen ? css.drawerOverlayOpen : ''}`}
          onClick={() => setDrawerOpen(false)}
        />

        {/* Drawer panel */}
        <div
          className={`${css.drawer} ${drawerOpen ? css.drawerOpen : ''}`}
          data-testid="nav-drawer-panel"
        >
          {navigatorContent}
        </div>
      </>
    );
  }

  // ── Default inline mode (desktop) ───────────────────────────────────────
  return navigatorContent;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  wrapper: {
    background: '#fff',
    border: '2px solid #333',
    borderRadius: '8px',
    overflow: 'hidden',
    fontSize: '0.82rem',
    userSelect: 'none',
  },

  // ── Header ────────────────────────────────────────────────────────────────
  header: {
    background: '#1a1a2e',
    color: '#fff',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    fontWeight: '700',
    letterSpacing: '0.8px',
    fontSize: '0.78rem',
    textTransform: 'uppercase',
  },
  toggleBtn: {
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.3)',
    color: '#fff',
    padding: '2px 8px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.68rem',
    lineHeight: 1.5,
  },

  // ── Summary strip ─────────────────────────────────────────────────────────
  summary: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    padding: '8px 12px',
    borderBottom: '1px solid #eee',
  },
  summaryItem: (type) => ({
    fontSize: '0.7rem',
    fontWeight: '600',
    color: type === 'answered' ? '#2e7d32'
         : type === 'skipped'  ? '#e07b00'
         : type === 'flagged'  ? '#1565c0'
         : '#888',
    background: type === 'answered' ? '#f1f8f1'
              : type === 'skipped'  ? '#fff3e0'
              : type === 'flagged'  ? '#e8f0fe'
              : '#f5f5f5',
    padding: '2px 7px',
    borderRadius: '10px',
  }),

  // ── Number grid ───────────────────────────────────────────────────────────
  // No inner scroll — the navigatorColumn wrapper in App.js owns overflow.
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(28px, 1fr))',
    gap: '4px',
    padding: '10px 10px 6px',
  },
  circle: {
    aspectRatio: '1 / 1',
    borderRadius: '50%',
    fontSize: '0.68rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    position: 'relative',
    transition: 'transform 0.08s ease, box-shadow 0.08s ease',
    outline: 'none',
    minWidth: 0,
  },
  flagPip: {
    position: 'absolute',
    top: '-3px',
    right: '-3px',
    fontSize: '0.45rem',
    lineHeight: 1,
    pointerEvents: 'none',
  },

  // ── Legend ────────────────────────────────────────────────────────────────
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px 10px',
    padding: '8px 12px 10px',
    borderTop: '1px solid #eee',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  legendDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  legendLabel: {
    fontSize: '0.65rem',
    color: '#666',
    whiteSpace: 'nowrap',
  },
};
